<template>
  <div class="relative select-none">
    <nav class="flex flex-wrap items-center justify-between px-2 bg-white dark:bg-stone-800 shadow-md">
      <!-- Brand section -->
      <div class="navbar-brand">
        <button @click="goHome" class="navbar-item flex items-center text-stone-800 dark:text-stone-100">
          <img src="/images/favicon-32x32.png" class="inline-block mr-3" alt="Spin the Names Logo" />
          <span class="font-cutedino text-2xl mt-1">Spin the Names!</span>
        </button>
      </div>

      <!-- Mobile burger - Changed from md to lg breakpoint -->
      <div class="xl:hidden flex justify-end flex-grow">
        <button @click="mobileMenuOpen = !mobileMenuOpen"
          class="p-2 text-stone-700 dark:text-stone-200 focus:outline-none">
          <i class="fas fa-bars text-xl"></i>
        </button>
      </div>

      <!-- Start items -->
      <div class="flex items-center">
        <span
          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 mt-4 ml-2"
          v-show="!online">
          {{ t('toolbar.Offline mode') }}
        </span>
      </div>

      <!-- End items - Changed from md to lg breakpoint -->
      <div class="hidden xl:flex items-center space-x-2">
        <!-- Base navigation buttons - Changed to always display but disable instead of hide -->
        <button :disabled="wheelIsBusy"
          :class="{ 'text-stone-400 dark:text-stone-600 cursor-not-allowed': wheelIsBusy }"
          v-show="!fullScreen && !sheetLinked" @click="emit('reset-wheel')" class="navbar-item">
          <i class="fas fa-file fa-fw"></i>&nbsp;{{ t('toolbar.New') }}
        </button>

        <button :disabled="wheelIsBusy"
          :class="{ 'text-stone-400 dark:text-stone-600 cursor-not-allowed': wheelIsBusy }"
          v-show="!fullScreen && !sheetLinked" @click="emit('open-open-dialog')" class="navbar-item">
          <i class="fa fa-folder-open fa-fw"></i>&nbsp;{{ t('common.Open') }}
        </button>

        <button :disabled="wheelIsBusy"
          :class="{ 'text-stone-400 dark:text-stone-600 cursor-not-allowed': wheelIsBusy }"
          v-show="online && !fullScreen" @click="emit('open-save-dialog')" class="navbar-item">
          <i class="fa fa-save fa-fw"></i>&nbsp;{{ t('common.Save') }}
        </button>

        <button :disabled="wheelIsBusy"
          :class="{ 'text-stone-400 dark:text-stone-600 cursor-not-allowed': wheelIsBusy }"
          v-show="online && !fullScreen" @click="emit('open-share-dialog')" class="navbar-item">
          <i class="fa fa-share-alt fa-fw"></i>&nbsp;{{ t('toolbar.Share') }}
        </button>

        <button :disabled="wheelIsBusy"
          :class="{ 'text-stone-400 dark:text-stone-600 cursor-not-allowed': wheelIsBusy }" v-show="!fullScreen"
          @click="emit('open-customize-dialog')" class="navbar-item">
          <i class="fas fa-palette fa-fw"></i>&nbsp;{{ t('common.Customize') }}
        </button>

        <button :disabled="wheelIsBusy"
          :class="{ 'text-stone-400 dark:text-stone-600 cursor-not-allowed': wheelIsBusy }" v-show="!fullScreen"
          @click="enterFullScreen()" class="navbar-item">
          <i class="fa fa-expand fa-fw"></i>&nbsp;{{ t('toolbar.Fullscreen') }}
        </button>

        <button v-show="exitFullscreenButtonVisible" @click="exitFullScreen()" class="navbar-item">
          <i class="fa fa-compress fa-fw"></i>&nbsp;{{ t('toolbar.Exit fullscreen') }}
        </button>

        <button v-show="unlinkSheetButtonVisible" @click="wheelStore.unlinkSheet()" class="navbar-item">
          <i class="fa fa-unlink fa-fw"></i>&nbsp;{{ t('toolbar.Unlink Google Spreadsheet') }}
        </button>

        <!-- IE fallback -->
        <template v-if="browserIsIEOrOldEdge">
          <!-- <button 
          v-show="feedbackbuttonVisible" 
          @click="openFeedbackForm"
          class="navbar-item"
        >
          <i class="fa fa-comment fa-fw"></i>&nbsp;{{ t('toolbar.Feedback') }}
        </button> -->
        </template>

        <!-- More dropdown - Added click outside to close dropdown -->
        <div v-else class="relative" v-show="online && !fullScreen">
          <button :disabled="wheelIsBusy"
            :class="{ 'text-stone-400 dark:text-stone-600 cursor-not-allowed': wheelIsBusy }"
            @click="toggleMoreDropdown" class="navbar-item flex items-center">
            {{ t('toolbar.More') }}
            <i class="fas fa-angle-down ml-1"></i>
          </button>

          <div v-show="moreDropdownOpen"
            class="absolute right-0 mt-2 w-48 bg-white dark:bg-stone-800 rounded-md shadow-lg py-1 z-10">
            <!-- <button 
            v-show="feedbackbuttonVisible" 
            @click="openFeedbackForm" 
            class="block w-full text-left px-4 py-2 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700"
          >
            <i class="fa fa-comment fa-fw"></i>&nbsp;{{ t('toolbar.Feedback') }}
          </button> -->

            <button v-show="faqbuttonVisible" @click="navigateTo('/about')"
              class="block w-full text-left px-4 py-2 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700">
              <i class="fa fa-info-circle fa-fw"></i>&nbsp;About
            </button>

            <button v-show="faqbuttonVisible" @click="navigateTo('/blog')"
              class="block w-full text-left px-4 py-2 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700">
              <i class="fa fa-newspaper fa-fw"></i>&nbsp;Blog
            </button>

            <button v-show="faqbuttonVisible" @click="navigateTo('/faq')"
              class="block w-full text-left px-4 py-2 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700">
              <i class="fa fa-question-circle fa-fw"></i>&nbsp;{{ t('toolbar.FAQ') }}
            </button>

            <button v-show="feedbackbuttonVisible" @click="navigateTo('/privacy-policy')"
              class="block w-full text-left px-4 py-2 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700">
              <i class="fas fa-user-secret fa-fw"></i>&nbsp;{{ t('common.Privacy policy') }}
            </button>

            <button v-show="feedbackbuttonVisible" @click="navigateTo('/terms-of-service')"
              class="block w-full text-left px-4 py-2 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700">
              <i class="fas fa-balance-scale fa-fw"></i>&nbsp;{{ t('appInfo.Terms of service') }}
            </button>

            <div class="border-t border-stone-200 dark:border-stone-700 my-1"></div>


            <button v-show="importVisible" @click="emit('open-sheet-dialog')"
              class="block w-full text-left px-4 py-2 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700">
              <i class="fa fa-link fa-fw"></i>&nbsp;{{ t('common.Link Google Spreadsheet') }}
            </button>

            <button v-if="!browserIsIEOrOldEdge" v-show="optionsButtonVisible" @click="emit('open-theme-dialog')"
              class="block w-full text-left px-4 py-2 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700">
              <i class="fas fa-palette fa-fw"></i>&nbsp;{{ t('toolbar.Theme') }}
            </button>

            <div class="border-t border-stone-200 dark:border-stone-700 my-1"></div>

            <button @click="emit('open-account-dialog')"
              class="block w-full text-left px-4 py-2 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700">
              <i class="fas fa-user-alt fa-fw"></i>&nbsp;{{ t('common.My account') }}
            </button>

            <!-- Admin-only navigation -->
            <div v-show="adminButtonsVisible" class="border-t border-stone-200 dark:border-stone-700 my-1"></div>

            <button v-show="adminButtonsVisible" @click="navigateTo('/admin')"
              class="block w-full text-left px-4 py-2 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700">
              <i class="fas fa-user-shield fa-fw"></i>&nbsp;Admin
            </button>

            <button v-show="adminButtonsVisible" @click="navigateTo('/carousel')"
              class="block w-full text-left px-4 py-2 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700">
              <i class="fas fa-images fa-fw"></i>&nbsp;Carousels
            </button>
          </div>
        </div>

        <!-- Language selection -->
        <div class="relative" v-if="isMobile">
          <button :disabled="wheelIsBusy"
            :class="{ 'text-stone-400 dark:text-stone-600 cursor-not-allowed': wheelIsBusy }"
            @click="languageDropdownOpen = !languageDropdownOpen" class="navbar-item flex items-center">
            {{ t('toolbar.Language') }}
            <i class="fas fa-angle-down ml-1"></i>
          </button>

          <div v-show="languageDropdownOpen"
            class="absolute right-0 mt-2 w-48 bg-white dark:bg-stone-800 rounded-md shadow-lg py-1 z-10">
            <button v-for="localeItem in locales" :key="localeItem.name" @click="emit('set-locale', localeItem.name)"
              class="block w-full text-left px-4 py-2 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700">
              {{ localeItem.humanName }}
            </button>
          </div>
        </div>

        <div v-if="!isMobile" v-show="online && !fullScreen" class="ml-2">
          <select :disabled="wheelIsBusy" v-model="currentLocale"
            class="select bg-white dark:bg-stone-800 border border-stone-300 dark:border-stone-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            :class="{ 'text-stone-400 dark:text-stone-600 border-stone-200 dark:border-stone-700 cursor-not-allowed': wheelIsBusy }">
            <option v-for="localeItem in locales" :value="localeItem.name" :key="localeItem.name">
              {{ localeItem.humanName }}
            </option>
            <option value='add-your-language'>
              {{ t('toolbar.Add your language') }}
            </option>
          </select>
        </div>

        <!-- Dark mode toggle removed - consolidated into theme dialog -->
      </div>
    </nav>
    <AnnouncementBanner />

    <!-- Mobile menu - Moved outside nav to maintain proper document flow -->
    <div v-show="mobileMenuOpen"
      class="xl:hidden absolute top-[48px] left-0 right-0 bg-white dark:bg-stone-800 shadow-md z-50">
      <div class="flex flex-col">
        <button v-show="newButtonVisible" @click="emit('reset-wheel'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fas fa-file fa-fw"></i>&nbsp;{{ t('toolbar.New') }}
        </button>

        <button v-show="openButtonVisible" @click="emit('open-open-dialog'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fa fa-folder-open fa-fw"></i>&nbsp;{{ t('common.Open') }}
        </button>

        <button v-show="saveButtonVisible" @click="emit('open-save-dialog'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fa fa-save fa-fw"></i>&nbsp;{{ t('common.Save') }}
        </button>

        <button v-show="shareButtonVisible" @click="emit('open-share-dialog'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fa fa-share-alt fa-fw"></i>&nbsp;{{ t('toolbar.Share') }}
        </button>

        <button v-show="optionsButtonVisible" @click="emit('open-customize-dialog'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fas fa-palette fa-fw"></i>&nbsp;{{ t('common.Customize') }}
        </button>

        <button v-show="fullscreenButtonVisible" @click="enterFullScreen(); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fa fa-expand fa-fw"></i>&nbsp;{{ t('toolbar.Fullscreen') }}
        </button>

        <button v-show="exitFullscreenButtonVisible" @click="exitFullScreen(); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fa fa-compress fa-fw"></i>&nbsp;{{ t('toolbar.Exit fullscreen') }}
        </button>

        <button v-show="unlinkSheetButtonVisible" @click="wheelStore.unlinkSheet(); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fa fa-unlink fa-fw"></i>&nbsp;{{ t('toolbar.Unlink Google Spreadsheet') }}
        </button>

        <!-- Mobile dropdown items -->
        <!-- <button 
          v-show="feedbackbuttonVisible" 
          @click="openFeedbackForm(); mobileMenuOpen = false;" 
          class="navbar-item-mobile"
        >
          <i class="fa fa-comment fa-fw"></i>&nbsp;{{ t('toolbar.Feedback') }}
        </button> -->

        <button v-show="faqbuttonVisible" @click="navigateTo('/about'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fa fa-info-circle fa-fw"></i>&nbsp;About
        </button>

        <button v-show="faqbuttonVisible" @click="navigateTo('/blog'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fa fa-newspaper fa-fw"></i>&nbsp;Blog
        </button>

        <button v-show="faqbuttonVisible" @click="navigateTo('/faq'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fa fa-question-circle fa-fw"></i>&nbsp;{{ t('toolbar.FAQ') }}
        </button>

        <button v-show="feedbackbuttonVisible" @click="navigateTo('/privacy-policy'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fas fa-user-secret fa-fw"></i>&nbsp;{{ t('common.Privacy policy') }}
        </button>

        <button v-show="feedbackbuttonVisible" @click="navigateTo('/terms-of-service'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fas fa-balance-scale fa-fw"></i>&nbsp;{{ t('appInfo.Terms of service') }}
        </button>


        <button v-show="importVisible" @click="emit('open-sheet-dialog'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fa fa-link fa-fw"></i>&nbsp;{{ t('common.Link Google Spreadsheet') }}
        </button>

        <button v-show="optionsButtonVisible" @click="emit('open-theme-dialog'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fas fa-palette fa-fw"></i>&nbsp;{{ t('toolbar.Theme') }}
        </button>

        <button @click="emit('open-account-dialog'); mobileMenuOpen = false;" class="navbar-item-mobile">
          <i class="fas fa-user-alt fa-fw"></i>&nbsp;{{ t('common.My account') }}
        </button>

        <!-- Admin-only navigation -->
        <button v-show="adminButtonsVisible" @click="navigateTo('/admin'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fas fa-user-shield fa-fw"></i>&nbsp;Admin
        </button>

        <button v-show="adminButtonsVisible" @click="navigateTo('/carousel'); mobileMenuOpen = false;"
          class="navbar-item-mobile">
          <i class="fas fa-images fa-fw"></i>&nbsp;Carousels
        </button>

        <!-- Language selection -->
        <div class="py-2 px-4 border-t border-stone-200 dark:border-stone-700">
          <span class="block font-medium mb-2 text-stone-700 dark:text-stone-300">
            {{ t('toolbar.Language') }}
          </span>
          <div class="flex flex-wrap gap-2">
            <button v-for="localeItem in locales" :key="localeItem.name"
              @click="emit('set-locale', localeItem.name); mobileMenuOpen = false;" class="btn btn-sm btn-secondary">
              {{ localeItem.humanName }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWheelStore } from '@/stores/useWheelStore';
import { useHardwareStore } from '@/stores/useHardwareStore';
import { usePreferencesStore } from '@/stores/usePreferencesStore';
import { useAdminStore } from '@/stores/useAdminStore';
import { useRouter } from 'vue-router';
import * as Util from '@utils/Util.js';
import * as Locales from '@utils/Locales.js';
import { Auth } from '@services/firebase';
import AnnouncementBanner from '@components/ui/AnnouncementBanner.vue';

// Setup composables
const { t, locale } = useI18n();
const wheelStore = useWheelStore();
const hardwareStore = useHardwareStore();
const preferencesStore = usePreferencesStore();
const adminStore = useAdminStore();
const router = useRouter();
const instance = getCurrentInstance();

// Define emits
const emit = defineEmits([
  'reset-wheel',
  'open-open-dialog',
  'open-save-dialog',
  'open-share-dialog',
  'open-customize-dialog',
  'open-sheet-dialog',
  'open-account-dialog',
  'open-theme-dialog',
  'set-locale'
]);

// Reactive data
const hasWindow = typeof window !== 'undefined';
const hasNavigator = typeof navigator !== 'undefined';

const toolbarBrand = ref(hasWindow ? window.location.host : '');
const browserIsIEOrOldEdge = ref(
  hasNavigator ? Util.browserIsIEOrOldEdge(navigator.userAgent) : false
);
const includeLocaleFlags = hasNavigator
  ? Util.platformSupportsFlags(navigator)
  : false;
const locales = ref(Locales.getNamesForAll(includeLocaleFlags));
const moreDropdownOpen = ref(false);
const languageDropdownOpen = ref(false);
const mobileMenuOpen = ref(false);
const mobileBreakpoint = ref(768);
const windowWidth = ref(hasWindow ? window.innerWidth : mobileBreakpoint.value);
const authStateUnsubscribe = ref(null);

// Computed properties
const isMobile = computed(() => windowWidth.value < mobileBreakpoint.value);

const currentLocale = computed({
  get: () => locale.value,
  set: (newValue) => {
    if (newValue == 'add-your-language') {
      router.push('/translate.html');
    } else {
      emit('set-locale', newValue);
    }
  }
});

// Store getters as computed properties
const online = computed(() => hardwareStore.isOnline);
const wheelIsBusy = computed(() => wheelStore.wheelIsBusy);
const fullScreen = computed(() => hardwareStore.isFullScreen);
const sheetLinked = computed(() => wheelStore.sheetLinked);
const version = computed(() => preferencesStore.version);
const isAdmin = computed(() => adminStore.isAdmin);

// Button visibility computed properties
const newButtonVisible = computed(() =>
  !fullScreen.value && !sheetLinked.value && !wheelIsBusy.value
);

const openButtonVisible = computed(() =>
  !fullScreen.value && !sheetLinked.value && !wheelIsBusy.value
);

const saveButtonVisible = computed(() =>
  online.value && !fullScreen.value && !wheelIsBusy.value
);

const shareButtonVisible = computed(() =>
  online.value && !fullScreen.value && !wheelIsBusy.value
);

const optionsButtonVisible = computed(() =>
  !fullScreen.value && !wheelIsBusy.value
);

const fullscreenButtonVisible = computed(() =>
  !fullScreen.value && !wheelIsBusy.value
);

const exitFullscreenButtonVisible = computed(() =>
  fullScreen.value
);

const moreVisible = computed(() =>
  online.value && !fullScreen.value && !wheelIsBusy.value
);

const importVisible = computed(() =>
  online.value && !fullScreen.value && !sheetLinked.value && !wheelIsBusy.value
);

const unlinkSheetButtonVisible = computed(() =>
  sheetLinked.value && !fullScreen.value && !wheelIsBusy.value
);

const languageVisible = computed(() =>
  online.value && !fullScreen.value && !wheelIsBusy.value
);

const faqbuttonVisible = computed(() =>
  !fullScreen.value && !wheelIsBusy.value
);

const feedbackbuttonVisible = computed(() =>
  online.value && !fullScreen.value && !wheelIsBusy.value
);

const adminButtonsVisible = computed(() =>
  isAdmin.value && online.value && !fullScreen.value && !wheelIsBusy.value
);

// Methods
const goHome = () => {
  router.push(`/${Util.getNonEnglishLocale(locale.value)}`);
};

const navigateTo = (route) => {
  router.push(route)
    .then(() => {
      moreDropdownOpen.value = false;
    })
    .catch(err => {
      console.error(`Navigation error for route ${route}:`, err);
      if (err.name !== 'NavigationDuplicated' && hasWindow) {
        window.location.href = route;
      }
    });
};

const enterFullScreen = () => {
  Util.trackEvent('Wheel', 'EnterFullscreen', '');
  hardwareStore.enterFullScreen();
};

const exitFullScreen = () => {
  Util.trackEvent('Wheel', 'ExitFullscreen', '');
  hardwareStore.exitFullScreen();
};

const toggleMoreDropdown = (event) => {
  event.stopPropagation();
  moreDropdownOpen.value = !moreDropdownOpen.value;
};

const closeDropdownsOnClickOutside = (event) => {
  if (!instance?.vnode.el) return;

  const moreDropdown = instance.vnode.el.querySelector('.relative');
  const languageDropdown = instance.vnode.el.querySelector('.relative:last-child');

  if (moreDropdownOpen.value && moreDropdown && !moreDropdown.contains(event.target)) {
    moreDropdownOpen.value = false;
  }

  if (languageDropdownOpen.value && languageDropdown && !languageDropdown.contains(event.target)) {
    languageDropdownOpen.value = false;
  }
};

const handleResize = () => {
  if (!hasWindow) return;
  windowWidth.value = window.innerWidth;
};

// Lifecycle hooks
onMounted(() => {
  if (typeof document !== 'undefined') {
    document.addEventListener('click', closeDropdownsOnClickOutside);
  }
  if (hasWindow) {
    window.addEventListener('resize', handleResize);
    handleResize();
  }
  adminStore.checkAdminStatus();

  authStateUnsubscribe.value = Auth.addAuthStateListener((user) => {
    if (user && !user.isAnonymous) {
      adminStore.checkAdminStatus();
    } else {
      adminStore.clearAdminStatus();
    }
  });
});

onBeforeUnmount(() => {
  if (typeof document !== 'undefined') {
    document.removeEventListener('click', closeDropdownsOnClickOutside);
  }
  if (hasWindow) {
    window.removeEventListener('resize', handleResize);
  }

  if (authStateUnsubscribe.value) {
    authStateUnsubscribe.value();
    authStateUnsubscribe.value = null;
  }
});
</script>

<style>
.navbar-brand {
  @apply flex items-center mr-6;
}

.navbar-item {
  @apply px-4 py-2 text-stone-700 dark:text-stone-200 hover:text-primary-500 dark:hover:text-primary-400 transition-all;
}

.navbar-item-mobile {
  @apply w-full text-left px-4 py-3 text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors;
}
</style>
